import { useEffect } from 'react';
import type { MarineOrganizationAccess } from './AuthGate';
import { supabase } from './lib/supabase';
import BrokerageWorkspaceV3 from './BrokerageWorkspaceV3';
import DocumentFieldEditorBridge from './DocumentFieldEditorBridge';
import ListingGalleryEnhancer from './ListingGalleryEnhancer';
import './document-editor.css';

function usesVygWebsiteSource(specs:any) {
  const system = String(specs?.source_system || '').toLowerCase();
  const source = String(specs?.source_listing_url || specs?.import_source_url || '').toLowerCase();
  return system === 'vyg_website' || source.includes('yachtsvancouver.com/');
}

export default function BrokerageWorkspaceV4({ organization }:{ organization:MarineOrganizationAccess }) {
  useEffect(()=>{
    let cancelled = false;

    async function syncImportedMediaInBackground() {
      if (!supabase || cancelled) return;
      try {
        const [listingResult,vesselResult] = await Promise.all([
          supabase.from('marine_listings').select('id,vessel_id').eq('organization_id',organization.id),
          supabase.from('marine_vessels').select('id,specifications').eq('organization_id',organization.id),
        ]);
        if (cancelled || listingResult.error || vesselResult.error) return;

        const now = Date.now();
        const vesselById = Object.fromEntries((vesselResult.data || []).map((row:any)=>[row.id,row]));
        const needsSync = (listingResult.data || []).some((listing:any)=>{
          const specs = vesselById[listing.vessel_id]?.specifications || {};

          // Vancouver Yacht Group has its own deterministic website-gallery
          // synchronizer. Do not send those records through the older
          // Boats.com recovery path or its status fields can overwrite the
          // successful website-media sync state.
          if (usesVygWebsiteSource(specs)) return false;

          const hasImportedSource = Boolean(
            specs.source_primary_image_url ||
            specs.source_listing_url ||
            specs.import_source_url
          );
          if (!hasImportedSource) return false;

          const complete = Number(specs.source_media_sync_version || 0) >= 3 && Boolean(specs.source_media_sync_completed_at);
          if (complete) return false;

          const retryAt = specs.source_media_sync_next_retry_at ? new Date(String(specs.source_media_sync_next_retry_at)).getTime() : 0;
          return !retryAt || Number.isNaN(retryAt) || retryAt <= now;
        });

        if (!needsSync || cancelled) return;

        // Photo recovery is maintenance work, not a login gate. The brokerage
        // workspace stays interactive while the secure backend checks for any
        // missing legacy source-gallery images.
        const { error } = await supabase.functions.invoke('marine-sync-listing-media',{
          body:{organization_id:organization.id},
        });
        if (error) console.warn('MarineOps listing media sync:',error.message);
      } catch (error) {
        console.warn('MarineOps listing media sync:',error);
      }
    }

    void syncImportedMediaInBackground();
    return ()=>{ cancelled = true; };
  },[organization.id]);

  return <>
    <BrokerageWorkspaceV3 organization={organization}/>
    <DocumentFieldEditorBridge organizationId={organization.id}/>
    <ListingGalleryEnhancer/>
  </>;
}
